import React from 'react';
import { Menu, MenuButton } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import Tooltip from 'flow/components/ui-components/Tooltip';
import {
    selectAssignee,
    selectTicketIsFetched,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { TicketKey } from 'flow/types/EntityKeys';
import AssigneePickerMenu from 'flow/components/tickets/Assignee/AssigneePickerMenu';
import useUpdateTicketAssignee from 'flow/components/tickets/Assignee/useUpdateTicketAssignee';

import TicketHeaderAssigneeButtonFace from './TicketHeaderAssigneeButtonFace';

interface Props {
    ticketKey: TicketKey;
}

const TicketHeaderAssigneeButton = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const assigneeKey = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );
    const canAssign = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'assign'),
    );
    const ticketIsFetched = useAppSelector((state) =>
        selectTicketIsFetched(state, ticketKey),
    );

    const { onSelect, isPerformingApiRequest } = useUpdateTicketAssignee(
        ticketKey,
        'ticket header',
    );

    if (!ticketIsFetched) return null;

    const disabled = !canAssign || isPerformingApiRequest;

    return (
        <Menu as="div" className="relative">
            {({ open }) => (
                <>
                    <Tooltip
                        label={
                            canAssign
                                ? undefined
                                : i18next.t('tickets.no_permission')
                        }
                    >
                        <MenuButton
                            disabled={disabled}
                            className={twMerge(
                                'flex h-10 items-center gap-1 rounded-lg bg-gray-200 px-2 text-xs font-semibold text-gray-800',
                                disabled
                                    ? 'pointer-events-none opacity-50'
                                    : 'hover:darken-10',
                            )}
                        >
                            <TicketHeaderAssigneeButtonFace
                                assigneeKey={assigneeKey}
                            />
                        </MenuButton>
                    </Tooltip>
                    {open ? (
                        <AssigneePickerMenu
                            ticketKey={ticketKey}
                            onSelect={onSelect}
                        />
                    ) : null}
                </>
            )}
        </Menu>
    );
};

export default React.memo(TicketHeaderAssigneeButton);
